import React, { useCallback, useEffect, useMemo, useState } from "react";
import { weeklyBoards, computeLeaderboard, currentWeekId, BOARD_COUNT } from "./weeklyBoards";
import { getWeekSeed, loadAllEntries, listWeeks, loadComments, saveComment } from "./tournamentData";
import { colourSuits } from "../UI/SuitText";
import { trickWinner } from "../PlayTable/bridgeCore";

// Weekly tournament results + board review (Paul, 2 Oct 2026): your score on each board, every week to look
// back on, and per board the four hands, everyone's result and auction, and Paul's comment.
// This week: a board opens once you have played it (Paul: "when they've played that board").
// Past weeks: everything is open.

const SEAT_NAME = { N: "North", E: "East", S: "South", W: "West" };
const SUIT_SYM = { S: "♠", H: "♥", D: "♦", C: "♣" };
const RANK_ORDER = "AKQJT98765432";
const signed = (n) => (n > 0 ? `+${n}` : `${n}`);

function shortName(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "Player";
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}`;
}

function strainText(s) {
  return s === "N" || s === "NT" ? "NT" : SUIT_SYM[s] || s;
}

export function contractText(e) {
  if (!e || e.passout || !e.contract) return "Passed out";
  const c = e.contract;
  const dbl = c.doubled === 2 ? "XX" : c.doubled === 1 ? "X" : "";
  const over = (e.declarerTricks || 0) - (6 + c.level);
  return `${c.level}${strainText(c.strain)}${dbl} ${e.declarer} ${over === 0 ? "=" : signed(over)}`;
}

function callText(call) {
  if (!call) return "";
  if (call.kind === "pass") return "Pass";
  if (call.kind === "double") return "X";
  if (call.kind === "redouble") return "XX";
  return `${call.level}${strainText(call.strain)}`;
}

function suitLine(cards, suit) {
  const r = cards.filter((c) => c.suit === suit).map((c) => c.rank)
    .sort((a, b) => RANK_ORDER.indexOf(a) - RANK_ORDER.indexOf(b))
    .map((x) => (x === "T" ? "10" : x)).join(" ");
  return r || "—";
}

function Hand({ seat, cards, dealer }) {
  return (
    <div className={`wr-hand wr-hand--${seat.toLowerCase()} ${seat === "S" ? "is-you" : ""}`}>
      <div className="wr-seat">
        {SEAT_NAME[seat]}{seat === "S" && " (You)"}
        {seat === dealer && <span className="wr-dealer" title="Dealer">D</span>}
      </div>
      {["S", "H", "D", "C"].map((s) => (
        <div key={s} className="wr-suit">
          <span className={`wr-sym wr-sym--${s.toLowerCase()}`}>{SUIT_SYM[s]}</span> {suitLine(cards, s)}
        </div>
      ))}
    </div>
  );
}

function Deal({ board }) {
  const vul = board.vul === "Both" ? "All" : board.vul || "None";
  return (
    <div className="wr-deal">
      <Hand seat="N" cards={board.hands.N} dealer={board.dealer} />
      <Hand seat="W" cards={board.hands.W} dealer={board.dealer} />
      <div className="wr-mid">Board {board.boardNo}<br />Vul {vul}</div>
      <Hand seat="E" cards={board.hands.E} dealer={board.dealer} />
      <Hand seat="S" cards={board.hands.S} dealer={board.dealer} />
    </div>
  );
}

function Auction({ entry, dealer }) {
  const calls = entry.auction || [];
  if (!calls.length) return null;
  const order = ["W", "N", "E", "S"];
  const lead = order.indexOf(dealer);
  const cells = [...Array(lead).fill(null), ...calls];
  return (
    <table className="wr-auction">
      <thead><tr>{order.map((s) => <th key={s}>{SEAT_NAME[s]}{s === "S" ? " (You)" : ""}</th>)}</tr></thead>
      <tbody>
        {Array.from({ length: Math.ceil(cells.length / 4) }, (_, r) => (
          <tr key={r}>{order.map((s, c) => { const x = cells[r * 4 + c]; return <td key={s}>{x ? colourSuits(callText(x.call)) : ""}</td>; })}</tr>
        ))}
      </tbody>
    </table>
  );
}

// Card play, trick by trick (Paul, 2 Nov 2026, for Pauline: "see how others bid and play the hands ...
// let the good scores be seen, the + scores"). Shown for any entry with plus IMPs, and always for your own.
// Columns are the four seats; the lead card is marked and the winning card highlighted; a running
// trick count follows each trick. Tricks are revealed one at a time so it reads like a replay.
const cardText = (c) => `${SUIT_SYM[c.suit]}${c.rank === "T" ? "10" : c.rank}`;

function tricksOf(entry) {
  const plays = entry.play || [];
  const strain = entry.contract ? entry.contract.strain : "N";
  const out = [];
  let ns = 0, ew = 0;
  for (let i = 0; i + 4 <= plays.length; i += 4) {
    const t = plays.slice(i, i + 4);
    const winner = trickWinner(t, strain);
    if (winner === "N" || winner === "S") ns += 1; else ew += 1;
    out.push({ plays: t, lead: t[0].seat, winner, ns, ew });
  }
  return out;
}

export function canShowPlay(entry, imps, uid) {
  return !!(entry && entry.play && entry.play.length >= 4) && (entry.uid === uid || imps > 0);
}

export function Play({ entry }) {
  const tricks = useMemo(() => tricksOf(entry), [entry]);
  const [shown, setShown] = useState(1);
  useEffect(() => setShown(1), [entry]);
  if (!tricks.length) return null;
  const order = ["W", "N", "E", "S"];
  const bySeat = (t) => Object.fromEntries(t.plays.map((p) => [p.seat, p]));
  return (
    <div className="wr-play">
      <div className="wr-playHead">
        <b>The play</b>
        <span className="wr-playCtl">
          {shown < tricks.length ? <button className="wt-btn wr-playBtn" onClick={() => setShown((n) => Math.min(tricks.length, n + 1))}>Next trick →</button> : null}
          {shown < tricks.length ? <button className="wt-btn wr-playBtn" onClick={() => setShown(tricks.length)}>Show all</button> : null}
          {shown > 1 ? <button className="wt-btn wr-playBtn" onClick={() => setShown(1)}>Start again</button> : null}
        </span>
      </div>
      <table className="wr-tricks">
        <thead><tr><th>#</th>{order.map((s) => <th key={s}>{SEAT_NAME[s]}</th>)}<th className="wr-tally">N-S / E-W</th></tr></thead>
        <tbody>
          {tricks.slice(0, shown).map((t, i) => {
            const m = bySeat(t);
            return (
              <tr key={i}>
                <td className="wr-tNo">{i + 1}</td>
                {order.map((s) => {
                  const p = m[s];
                  if (!p) return <td key={s} />;
                  const cls = `wr-card wr-card--${p.card.suit.toLowerCase()} ${s === t.winner ? "is-winner" : ""}`;
                  return <td key={s}><span className={cls}>{s === t.lead && <span className="wr-lead" title="Led">•</span>}{cardText(p.card)}</span></td>;
                })}
                <td className="wr-tally">{t.ns} / {t.ew}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="wr-hint">• marks the card led; the winning card is boxed. Trick {Math.min(shown, tricks.length)} of {tricks.length}.{shown >= tricks.length && tricks.length < 13 ? " The remaining tricks were claimed." : ""}</p>
    </div>
  );
}

function CommentEditor({ weekId, boardNo, value, onSaved }) {
  const [text, setText] = useState(value || "");
  const [state, setState] = useState(null);
  useEffect(() => setText(value || ""), [value, boardNo]);
  const save = async () => {
    setState("saving");
    try {
      await saveComment(weekId, boardNo, text);
      onSaved(text.trim());
      setState("saved");
    } catch {
      setState("error");
    }
  };
  return (
    <div className="wr-editor">
      <label htmlFor="wr-comment">Your comment on board {boardNo} (admin only)</label>
      <textarea id="wr-comment" className="browser-default" rows={5} value={text} onChange={(e) => { setText(e.target.value); setState(null); }} />
      <button className="wt-btn wt-btn--primary" onClick={save} disabled={state === "saving"}>Save comment</button>
      {state === "saved" && <span className="wr-saved">Saved</span>}
      {state === "error" && <span className="wr-err">Couldn't save</span>}
    </div>
  );
}

export default function WeekResults({ initialWeekId, uid, isAdmin, onClose }) {
  const thisWeek = currentWeekId();
  const [weekId, setWeekId] = useState(initialWeekId || thisWeek);
  const [weeks, setWeeks] = useState([]);
  const [data, setData] = useState(null); // { boards, entries, lb, comments }
  const [review, setReview] = useState(null); // boardNo under review
  const [openRow, setOpenRow] = useState(null);

  useEffect(() => {
    listWeeks().then((w) => setWeeks(w.filter((x) => x <= thisWeek))).catch(() => setWeeks([]));
  }, [thisWeek]);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setReview(null);
    (async () => {
      try {
        const [seed, entries, comments] = await Promise.all([getWeekSeed(weekId), loadAllEntries(weekId), loadComments(weekId)]);
        if (cancelled) return;
        setData({ boards: weeklyBoards(seed, BOARD_COUNT, weekId), entries, lb: computeLeaderboard(entries), comments });
      } catch {
        if (!cancelled) setData({ error: true });
      }
    })();
    return () => { cancelled = true; };
  }, [weekId]);

  const isCurrent = weekId === thisWeek;
  const mine = useMemo(() => {
    const m = {};
    if (data && data.entries) data.entries.forEach((e) => { if (e.uid === uid) m[e.boardNo] = e; });
    return m;
  }, [data, uid]);
  const canOpen = useCallback((boardNo) => !isCurrent || isAdmin || !!mine[boardNo], [isCurrent, isAdmin, mine]);

  const weekPicker = (
    <select className="wr-week browser-default" value={weekId} onChange={(e) => setWeekId(e.target.value)} aria-label="Week">
      {(weeks.includes(weekId) ? weeks : [weekId, ...weeks]).map((w) => (
        <option key={w} value={w}>{w === thisWeek ? `This week (${w})` : `Week of ${w}`}</option>
      ))}
    </select>
  );

  if (!data) return <div className="wt-page"><p className="wt-wait">Loading…</p></div>;
  if (data.error) return <div className="wt-page"><p className="wt-empty">Couldn't load that week.</p><button className="wt-btn" onClick={onClose}>← Back</button></div>;

  const { boards, entries, lb, comments } = data;
  const imp = (e) => lb.impByUidBoard[`${e.uid}__${e.boardNo}`] || 0;
  const me = lb.players.find((p) => p.uid === uid);
  const myRank = me ? lb.players.indexOf(me) + 1 : null;

  // ── one board ──
  if (review != null) {
    const b = boards.find((x) => x.boardNo === review);
    const list = entries.filter((e) => e.boardNo === review).sort((x, y) => (y.rawScoreNS || 0) - (x.rawScoreNS || 0));
    const comment = comments[String(review)] || comments[review];
    const myEntry = mine[review];
    return (
      <div className="wt-page wr-page">
        <div className="wt-bar">
          <button className="wt-btn" onClick={() => setReview(null)}>← All boards</button>
          <h1 className="wt-title">Board {review}</h1>
          <span className="wt-week">{isCurrent ? "This week" : `Week of ${weekId}`}</span>
        </div>
        <div className="wr-nav">
          {boards.map((x) => (
            <button key={x.boardNo} className={`wr-chip ${x.boardNo === review ? "is-on" : ""}`} disabled={!canOpen(x.boardNo)} onClick={() => { setReview(x.boardNo); setOpenRow(null); }}>{x.boardNo}</button>
          ))}
        </div>
        <Deal board={b} />
        {myEntry && (
          <div className="wr-mine">
            <div><b>You:</b> {colourSuits(contractText(myEntry))} · {myEntry.rawScoreNS > 0 ? "+" : ""}{myEntry.rawScoreNS} · <b>{signed(imp(myEntry))} IMPs</b></div>
            <Auction entry={myEntry} dealer={b.dealer} />
            {canShowPlay(myEntry, imp(myEntry), uid) && <Play entry={myEntry} />}
          </div>
        )}
        {comment && (
          <div className="wr-comment">
            <b>Paul's comment</b>
            {/<[a-z][\s\S]*>/i.test(comment)
              ? <div className="wr-commentHtml" dangerouslySetInnerHTML={{ __html: comment }} /> /* written by Paul (admin-only rules) */
              : <div className="wr-commentText">{colourSuits(comment)}</div>}
          </div>
        )}
        {isAdmin && <CommentEditor weekId={weekId} boardNo={review} value={comment} onSaved={(t) => setData((d) => ({ ...d, comments: { ...d.comments, [String(review)]: t } }))} />}
        <h2 className="wr-h2">Everyone's results ({list.length})</h2>
        <table className="wr-results">
          <thead><tr><th>Player</th><th>Contract</th><th>Score</th><th>IMPs</th></tr></thead>
          <tbody>
            {list.map((e) => (
              <React.Fragment key={e.uid}>
                <tr className={`${e.uid === uid ? "is-me" : ""} wr-click`} onClick={() => setOpenRow(openRow === e.uid ? null : e.uid)} title={canShowPlay(e, imp(e), uid) ? "Show the auction and the play" : "Show the auction"}>
                  <td>{e.pair && e.pair.names ? `${e.uid === uid ? "You" : shortName(e.pair.names.S)} & ${shortName(e.pair.names.N)}` : e.uid === uid ? "You" : shortName(e.displayName)}</td>
                  <td>{colourSuits(contractText(e))}</td>
                  <td>{e.rawScoreNS > 0 ? "+" : ""}{e.rawScoreNS}</td>
                  <td><b>{signed(imp(e))}</b>{canShowPlay(e, imp(e), uid) && e.uid !== uid && <span className="wr-playTag">play</span>}</td>
                </tr>
                {openRow === e.uid && (
                  <tr className="wr-auctionRow"><td colSpan={4}>
                    <Auction entry={e} dealer={b.dealer} />
                    {canShowPlay(e, imp(e), uid) && <Play entry={e} />}
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        <p className="wr-hint">Tap a row to see that player's auction. Rows marked <span className="wr-playTag">play</span> (plus scores) also show the card play, trick by trick.</p>
      </div>
    );
  }

  // ── the week's boards ──
  return (
    <div className="wt-page wr-page">
      <div className="wt-bar">
        <button className="wt-btn" onClick={onClose}>← Back</button>
        <h1 className="wt-title">Results &amp; hands</h1>
        {weekPicker}
      </div>
      {me && <p className="wr-total">You: <b>{signed(me.totalImps)} IMPs</b> · {myRank} of {lb.players.length}{isCurrent ? " so far" : ""}</p>}
      <ul className="wt-boards">
        {boards.map((b) => {
          const e = mine[b.boardNo];
          const open = canOpen(b.boardNo);
          return (
            <li key={b.boardNo} className={`wt-boardItem ${open ? "wr-click" : "is-locked"}`} onClick={open ? () => setReview(b.boardNo) : undefined}>
              <span className="wt-bNo">{b.boardNo}</span>
              <span className="wt-bMeta">{e ? colourSuits(contractText(e)) : isCurrent ? "Not played yet" : "You didn't play this one"}</span>
              <span className="wt-bStatus">
                {e ? <span className="wt-result">{signed(imp(e))} IMPs</span> : null}
                {open ? <span className="wr-view">See results →</span> : <span className="wt-locked">Opens once you've played it</span>}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
